import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { Icon, type IconName } from '@/components/icon';

const alertVariants = cva('flex gap-3 rounded-lg border p-4 text-sm', {
  variants: {
    variant: {
      info: 'border-info/30 bg-info-subtle',
      success: 'border-success/30 bg-success-subtle',
      warning: 'border-warning/30 bg-warning-subtle',
      danger: 'border-danger/30 bg-danger-subtle',
    },
  },
  defaultVariants: { variant: 'info' },
});

type AlertVariant = 'info' | 'success' | 'warning' | 'danger';
const VARIANT_ICON: Record<AlertVariant, IconName> = {
  info: 'info',
  success: 'check-circle',
  warning: 'alert-triangle',
  danger: 'x-circle',
};
const VARIANT_TONE: Record<AlertVariant, string> = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertVariants> {
  title?: React.ReactNode;
  icon?: IconName;
}

export function Alert({
  className,
  variant,
  title,
  icon,
  children,
  ...props
}: AlertProps): JSX.Element {
  const v: AlertVariant = variant ?? 'info';
  return (
    <div role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      <Icon
        name={icon ?? VARIANT_ICON[v]}
        size={18}
        aria-hidden
        className={cn('mt-0.5 shrink-0', VARIANT_TONE[v])}
      />
      <div className="min-w-0">
        {title ? <div className="font-medium text-foreground">{title}</div> : null}
        {children ? <div className="text-muted-foreground">{children}</div> : null}
      </div>
    </div>
  );
}
